import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'antd';
import { Warning } from '@phosphor-icons/react';
import { isIgs, isLightreach } from './productsFinanceHubHelpers';
import DashboardModal from './DashboardModal';
import { OBD_FINANCE_PARTNER_CONFIRM_Z } from './oneButtonOverlayZ';
import './oneButton-css/FinancePartnerSyncConfirmModal.css';

export const NO_FP_SELECTED_SYNC_MESSAGE = 'no FP selected for sync, canceling the request';

const NONE_SELECTED_CLOSE_MS = 2000;

export function financePartnerConfirmLabel(name: string | null | undefined): string {
    if (isLightreach(name)) return 'Lightreach';
    if (isIgs(name)) return 'IGS';
    return String(name || '').trim();
}

export function uniqueFinancePartnerConfirmLabels(names: (string | null | undefined)[]): string[] {
    const seen = new Set<string>();
    const labels: string[] = [];
    for (const name of names) {
        const label = financePartnerConfirmLabel(name);
        if (!label) continue;
        const key = label.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        labels.push(label);
    }
    return labels;
}

export function leadMatchesConfirmLabel(financePartnerName: string | null | undefined, label: string): boolean {
    if (label === 'IGS') return isIgs(financePartnerName);
    if (label === 'Lightreach') return isLightreach(financePartnerName);
    return financePartnerConfirmLabel(financePartnerName).toLowerCase() === label.toLowerCase();
}

export function partnerTitleFromConfirmLabels(labels: string[]): string {
    if (labels.length !== 1) return '';
    return labels[0] === 'Lightreach' ? 'LightReach' : labels[0];
}

export function confirmSyncSourceLabel(stepId?: string | null): string {
    switch (stepId) {
        case 'utility':
            return 'Utility';
        case 'leadSystem':
            return 'Lead System';
        case 'contact':
            return 'Contact';
        case 'titleCheck':
            return 'Title';
        case 'lead':
            return 'Lead';
        default:
            return '';
    }
}

type FinancePartnerSyncConfirmModalProps = {
    open: boolean;
    partners: string[];
    theme?: 'dark' | 'light';
    sourceLabel?: string;
    onCancel: () => void;
    onConfirm: (selected: string[]) => void;
};

export const FinancePartnerSyncConfirmModal: React.FC<FinancePartnerSyncConfirmModalProps> = ({
    open,
    partners,
    theme = 'dark',
    sourceLabel,
    onCancel,
    onConfirm,
}) => {
    const [checked, setChecked] = useState<string[]>([]);
    const [noneSelected, setNoneSelected] = useState(false);
    const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearCloseTimer = () => {
        if (closeTimerRef.current != null) {
            clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
    };

    useEffect(() => {
        if (!open) {
            clearCloseTimer();
            setNoneSelected(false);
            return;
        }
        setChecked([...partners]);
        setNoneSelected(false);
    }, [open, partners]);

    useEffect(() => () => clearCloseTimer(), []);

    const toggle = (label: string) => {
        if (noneSelected) return;
        setChecked((current) =>
            current.includes(label) ? current.filter((item) => item !== label) : [...current, label],
        );
    };

    const handleCancel = () => {
        clearCloseTimer();
        setNoneSelected(false);
        onCancel();
    };

    const handleSyncClick = () => {
        if (noneSelected) return;
        if (checked.length === 0) {
            setNoneSelected(true);
            closeTimerRef.current = setTimeout(() => {
                closeTimerRef.current = null;
                setNoneSelected(false);
                onCancel();
            }, NONE_SELECTED_CLOSE_MS);
            return;
        }
        onConfirm(checked);
    };

    return (
        <DashboardModal
            open={open}
            onClose={handleCancel}
            title={sourceLabel ? `Confirm finance partner sync (${sourceLabel})` : 'Confirm finance partner sync'}
            width={440}
            maskClosable={false}
            zIndex={OBD_FINANCE_PARTNER_CONFIRM_Z}
            theme={theme}
            className="obd-fp-sync-confirm"
            rootClassName={`obd-fp-sync-confirm-root obd-theme-${theme}`}
            destroyOnClose
            footer={(
                <>
                    <Button onClick={handleCancel}>Cancel</Button>
                    <Button type="primary" onClick={handleSyncClick} disabled={noneSelected}>
                        Sync
                    </Button>
                </>
            )}
        >
            <div className="obd-fp-sync-confirm__body">
                <p className="obd-fp-sync-confirm__copy">
                    Confirm you are trying to sync with the finance partner(s) below.
                </p>
                <ul className="obd-fp-sync-confirm__list">
                    {partners.map((label) => (
                        <li key={label}>
                            <label className="obd-fp-sync-confirm__item">
                                <input
                                    type="checkbox"
                                    className="obd-fp-sync-confirm__checkbox"
                                    checked={checked.includes(label)}
                                    onChange={() => toggle(label)}
                                    disabled={noneSelected}
                                />
                                {label}
                            </label>
                        </li>
                    ))}
                </ul>
                {noneSelected && (
                    <div className="obd-fp-sync-confirm__error" role="alert">
                        <Warning size={16} weight="fill" />
                        {NO_FP_SELECTED_SYNC_MESSAGE}
                    </div>
                )}
            </div>
        </DashboardModal>
    );
};

export default FinancePartnerSyncConfirmModal;
